import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useCreateOrganizationContact } from '@/hooks';

const addContactSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  title: z.string().optional(),
  is_primary: z.boolean().default(false),
});

type AddContactFormData = z.infer<typeof addContactSchema>;

interface AddContactModalProps {
  open: boolean;
  organizationId: string;
  organizationName?: string; // 9.7d: Optional org name for display
  projectId?: string; // 9.21i: Auto-add contact to client scope on this project
  onOpenChange: (open: boolean) => void;
  onContactAdded: () => void;
  // 9.7d/9.18j: Optional callback with contact data for ScopeCard/Invoice integration
  onContactCreated?: (contactId: string, contactName: string, contactEmail?: string) => void;
}

export function AddContactModal({
  open,
  organizationId,
  organizationName,
  projectId, // 9.21i
  onOpenChange,
  onContactAdded,
  onContactCreated,
}: AddContactModalProps) {
  const { control, handleSubmit, formState: { errors }, reset } = useForm<AddContactFormData>({
    resolver: zodResolver(addContactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      title: '',
      is_primary: false,
    },
  });

  const createContact = useCreateOrganizationContact();

  const onSubmit = async (data: AddContactFormData) => {
    try {
      const newContact = await createContact.mutateAsync({
        organizationId,
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        title: data.title || undefined,
        is_primary_contact: data.is_primary,
        project_id: projectId,  // 9.21i: Auto-add to client scope
      });
      onContactAdded();
      // 9.7d/9.18j: Call onContactCreated with new contact data if provided
      // Note: API returns { success, contact: { id, ... } } - access contact.id
      if (onContactCreated && newContact?.contact?.id) {
        onContactCreated(newContact.contact.id, data.name, data.email || undefined);
      }
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to add contact:', error);
    }
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Contact</DialogTitle>
          <DialogDescription>
            Add a new contact{organizationName ? ` to ${organizationName}` : ' for this client'}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="name">Name *</Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  id="name"
                  placeholder="Enter contact name"
                  {...field}
                />
              )}
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email">Email <span className="text-gray-500 text-sm">(Optional)</span></Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  id="email"
                  type="email"
                  placeholder="contact@example.com"
                  {...field}
                />
              )}
            />
            {errors.email && (
              <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone">Phone <span className="text-gray-500 text-sm">(Optional)</span></Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Input
                  id="phone"
                  type="tel"
                  placeholder="555-1234"
                  {...field}
                />
              )}
            />
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="title">Title <span className="text-gray-500 text-sm">(Optional)</span></Label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input
                  id="title"
                  placeholder="e.g., Project Manager"
                  {...field}
                />
              )}
            />
          </div>

          {/* Primary Contact */}
          <div className="flex items-center space-x-2">
            <Controller
              name="is_primary"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="is_primary"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label
              htmlFor="is_primary"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Set as primary contact
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit">Add Contact</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
